import { clsx } from '@alexis/helpers/clsx';
import LogSliderWithDistribution from '@pages/FlightSearchResult/components/LogSliderWithDistribution';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PillToggle, { PillToggleItem } from '@components/PillToggle/PillToggle';
import Price from '@components/Price';

import { getRange } from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { changeShowTotalPrice } from '@redux/actions/priceOptionsActions';
import {
  getCurrencyState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type NewPriceFilterProps = {
  isPadded?: boolean;
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: any;
};

const NewPriceFilter: React.FC<NewPriceFilterProps> = ({
  isPadded,
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
}) => {
  const translations = useSelector(getTranslationsState);
  const priceOptions = useSelector(getPriceOptionsState);
  const dispatch = useDispatch();
  const { activeLeg, pricesFilter, changeMultiCityRangeFilter } = useFlightSearchFilters();
  const { isPerLegSearch } = useFlightPLS();

  const { exchangeRate, minUsdPrice, maxUsdPrice, usdPrices } = useFlightSearchResults();

  const priceOptionPillToggleItems = useMemo<PillToggleItem[]>(() => {
    return [
      {
        label: translations.price_per_person as string,
        selected: !priceOptions.showTotalPrice,
        onClick: () => {
          dispatch(changeShowTotalPrice(false));
        },
      },
      {
        label: translations.total_price as string,
        selected: priceOptions.showTotalPrice,
        onClick: () => {
          dispatch(changeShowTotalPrice(true));
        },
      },
    ];
  }, [
    dispatch,
    priceOptions.showTotalPrice,
    translations.price_per_person,
    translations.total_price,
  ]);

  const selectedPriceRange = useMemo(
    () =>
      pricesFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))?.values || [],
    [pricesFilter, activeLeg, isPerLegSearch],
  );

  const [isPriceTypeDropdownExpanded, setIsPriceTypeDropdownExpanded] = useState(false);
  const [newSelectedPrices, setNewSelectedPrices] = useState<number[]>(selectedPriceRange);

  const locale = useSelector(getLocaleState);
  const currency = useSelector(getCurrencyState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const priceSliderMinimumDistance = (() => {
    if (!!minUsdPrice && !!maxUsdPrice) {
      const priceRange = maxUsdPrice - minUsdPrice;

      if (priceRange > 1000) {
        return 20;
      }

      if (priceRange < 1001) {
        return 10;
      }

      if (priceRange < 101) {
        return 1;
      }
    }

    return 0;
  })();

  const selectedPrices = useMemo(() => {
    return selectedPriceRange.length ? selectedPriceRange : [minUsdPrice || 0, maxUsdPrice || 0];
  }, [maxUsdPrice, minUsdPrice, selectedPriceRange]);

  useEffect(() => {
    setNewSelectedPrices(selectedPrices);
  }, [selectedPrices]);

  useEffect(() => {
    if (isPriceTypeDropdownExpanded) {
      const handleBodyOnClick = (): void => {
        setIsPriceTypeDropdownExpanded(false);
      };

      document.body.addEventListener('click', handleBodyOnClick);

      return () => {
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isPriceTypeDropdownExpanded]);

  const handleSelectedPricesChanged = (prices: number[]) => {
    const newFilter = changeMultiCityRangeFilter({
      activeLeg: isPerLegSearch ? activeLeg : 0,
      searchParamKey: FlightSearchResultSearchParams.Price,
      max: maxUsdPrice,
      min: minUsdPrice,
      range: prices,
    });

    const selected_amount_min_usd = prices[0];
    const selected_amount_max_usd = prices[1];
    const isPriceFilterApplied = !!newFilter.length;
    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      price_min_max_usd: isPriceFilterApplied ? [minUsdPrice, maxUsdPrice] : undefined,
      price_selected_usd: isPriceFilterApplied
        ? [selected_amount_min_usd, selected_amount_max_usd]
        : undefined,
    });
    genzoTrackApplyFilterSearchView();
  };

  return (
    <div className={clsx(styles.categoryContent, styles.newPriceFilter, isPadded && styles.padded)}>
      <PillToggle items={priceOptionPillToggleItems} className={styles.pillToggle} />

      <div className={styles.sliderContainer} data-testid='price-slider-container'>
        <div className={styles.labelRange}>
          <Price
            isInline
            className={styles.label}
            price={{
              amount: newSelectedPrices[0] ?? minUsdPrice,
              amountUsd: newSelectedPrices[0] ?? minUsdPrice,
              currencyCode: 'USD',
            }}
            locale={locale}
            currency={currency}
            exchangeRate={exchangeRate}
          />
          <Price
            isInline
            className={styles.label}
            price={{
              amount: newSelectedPrices[1] ?? maxUsdPrice,
              amountUsd: newSelectedPrices[1] ?? maxUsdPrice,
              currencyCode: 'USD',
            }}
            locale={locale}
            currency={currency}
            exchangeRate={exchangeRate}
          />
        </div>

        <LogSliderWithDistribution
          points={usdPrices}
          dataPw='flightFilter_price'
          max={maxUsdPrice}
          min={minUsdPrice}
          onChange={(values, activeThumb) => {
            const priceRange = getRange({
              max: maxUsdPrice,
              min: minUsdPrice,
              activeThumb,
              values,
              minimumDistance: priceSliderMinimumDistance,
            });

            if (priceRange) setNewSelectedPrices(priceRange);
          }}
          onChangeCommitted={(values, activeThumb) => {
            recordChangeFilterAction();

            const priceRange = getRange({
              max: maxUsdPrice,
              min: minUsdPrice,
              activeThumb,
              values,
              minimumDistance: priceSliderMinimumDistance,
            });

            if (priceRange) {
              handleSelectedPricesChanged(priceRange);
            }
          }}
          value={newSelectedPrices}
        />
      </div>
    </div>
  );
};

export default NewPriceFilter;
